import { useEffect, useMemo, useState } from "react";
import { Loader2, Save, Mail } from "lucide-react";
import { toast } from "sonner";
import { fetchFees, updateClientProfile, sendClientEstimate, resendWelcomeEmail, formatApiError } from "@/lib/api";
import { SatisfactionMeter } from "@/components/portal/SatisfactionMeter";
import { COMPLEXITY_ITEMS, COMPLEXITY_CATEGORIES } from "@/data/content";
import StatePicker from "@/components/StatePicker";

const inputCls =
  "w-full border border-steel-border bg-steel-elevated px-4 py-3 text-sm text-steel-text placeholder:text-steel-muted transition-colors duration-300 focus:border-steel-accent focus:outline-none rounded-none";

const label = "mb-2 block font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted";

const FILING_STATUSES = [
  { id: "single", label: "Single" },
  { id: "married-jointly", label: "Married filing jointly" },
  { id: "married-separately", label: "Married filing separately" },
  { id: "head-of-household", label: "Head of household" },
  { id: "qualifying-surviving-spouse", label: "Qualifying surviving spouse" },
];

const CONTACT_METHODS = [
  { id: "email", label: "Email" },
  { id: "phone", label: "Phone call" },
  { id: "text", label: "Text message" },
];

const SELECTABLE = COMPLEXITY_ITEMS.filter((i) => i.id !== "w2" && i.id !== "multi-state");

export default function ClientDetailsTab({ adminKey, client, onSaved }) {
  const p = client.profile || {};
  const [fees, setFees] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [resending, setResending] = useState(false);
  const [newTempPassword, setNewTempPassword] = useState("");
  const [form, setForm] = useState({
    name: client.name || "",
    phone: p.phone || "",
    address: p.address || "",
    filing_status: p.filing_status || "single",
    dependents: p.dependents ?? 0,
    occupation: p.occupation || "",
    date_of_birth: p.date_of_birth || "",
    spouse_name: p.spouse_name || "",
    spouse_dob: p.spouse_dob || "",
    preferred_contact: p.preferred_contact || "email",
    referral_source: p.referral_source || "",
    notes: p.notes || "",
    ssn_itin: "",
    spouse_ssn: "",
  });
  const [selected, setSelected] = useState(p.selected_items || []);
  const [states, setStates] = useState(p.states || []);

  useEffect(() => {
    fetchFees().then(setFees).catch(() => setFees(null));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleItem = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const estimate = useMemo(() => {
    if (!fees) return null;
    const feeFor = (id) => (fees.items || []).find((f) => f.id === id);
    let min = Number(fees.base_min) || 0;
    let max = Number(fees.base_max) || 0;
    for (const id of selected) {
      const f = feeFor(id);
      if (f) {
        min += Number(f.min) || 0;
        max += Number(f.max) || 0;
      }
    }
    if (states.length > 0) {
      const ms = feeFor("multi-state");
      if (ms) {
        min += states.length * (Number(ms.min) || 0);
        max += states.length * (Number(ms.max) || 0);
      }
    }
    return { min, max };
  }, [fees, selected, states]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        phone: form.phone,
        address: form.address,
        filing_status: form.filing_status,
        dependents: Number(form.dependents) || 0,
        occupation: form.occupation,
        date_of_birth: form.date_of_birth,
        spouse_name: form.spouse_name,
        spouse_dob: form.spouse_dob,
        preferred_contact: form.preferred_contact,
        referral_source: form.referral_source,
        notes: form.notes,
        selected_items: selected,
        states,
      };
      if (form.ssn_itin.trim()) payload.ssn_itin = form.ssn_itin.trim();
      if (form.spouse_ssn.trim()) payload.spouse_ssn = form.spouse_ssn.trim();
      const data = await updateClientProfile(adminKey, client.user_id, payload);
      toast.success("Client details saved.");
      set("ssn_itin", "");
      set("spouse_ssn", "");
      onSaved?.(data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const emailEstimate = async () => {
    setSending(true);
    try {
      await sendClientEstimate(adminKey, client.user_id);
      toast.success("Updated estimate emailed to the client.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  const resendWelcome = async () => {
    setResending(true);
    try {
      const data = await resendWelcomeEmail(adminKey, client.user_id);
      setNewTempPassword(data.temp_password);
      toast.success("Welcome email resent with a new temporary password.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="mt-6 grid grid-cols-1 gap-10 lg:grid-cols-12" data-testid="admin-client-details">
      <div className="lg:col-span-7 space-y-8">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={label}>Full name</label>
            <input data-testid="cd-name" className={inputCls} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div>
            <label className={label}>Phone</label>
            <input data-testid="cd-phone" className={inputCls} value={form.phone} onChange={(e) => set("phone", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={label}>Address</label>
            <input data-testid="cd-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} />
          </div>
          <div>
            <label className={label}>Date of birth</label>
            <input data-testid="cd-dob" className={inputCls} value={form.date_of_birth} onChange={(e) => set("date_of_birth", e.target.value)} placeholder="MM/DD/YYYY" />
          </div>
          <div>
            <label className={label}>Occupation</label>
            <input data-testid="cd-occupation" className={inputCls} value={form.occupation} onChange={(e) => set("occupation", e.target.value)} />
          </div>
          <div>
            <label className={label}>Filing status</label>
            <select data-testid="cd-filing-status" className={inputCls} value={form.filing_status} onChange={(e) => set("filing_status", e.target.value)}>
              {FILING_STATUSES.map((f) => <option key={f.id} value={f.id}>{f.label}</option>)}
            </select>
          </div>
          <div>
            <label className={label}>Dependents</label>
            <input data-testid="cd-dependents" type="number" min="0" max="30" className={inputCls} value={form.dependents} onChange={(e) => set("dependents", e.target.value)} />
          </div>
          <div>
            <label className={label}>SSN / ITIN — last 4 {p.ssn_masked ? `(on file: ${p.ssn_masked})` : ""}</label>
            <input data-testid="cd-ssn" className={inputCls} value={form.ssn_itin} onChange={(e) => set("ssn_itin", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder={p.ssn_masked ? "Enter last 4 to replace" : "Last 4 (e.g. 6789)"} inputMode="numeric" maxLength={4} />
          </div>
          <div>
            <label className={label}>Preferred contact</label>
            <select data-testid="cd-preferred-contact" className={inputCls} value={form.preferred_contact} onChange={(e) => set("preferred_contact", e.target.value)}>
              {CONTACT_METHODS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
        </div>

        <div>
          <p className="mb-3 text-sm font-medium text-steel-text">Spouse</p>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className={label}>Spouse name</label>
              <input data-testid="cd-spouse-name" className={inputCls} value={form.spouse_name} onChange={(e) => set("spouse_name", e.target.value)} />
            </div>
            <div>
              <label className={label}>Spouse date of birth</label>
              <input data-testid="cd-spouse-dob" className={inputCls} value={form.spouse_dob} onChange={(e) => set("spouse_dob", e.target.value)} placeholder="MM/DD/YYYY" />
            </div>
            <div className="sm:col-span-2">
              <label className={label}>Spouse SSN / ITIN — last 4 {p.spouse_ssn_masked ? `(on file: ${p.spouse_ssn_masked})` : ""}</label>
              <input data-testid="cd-spouse-ssn" className={inputCls} value={form.spouse_ssn} onChange={(e) => set("spouse_ssn", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder={p.spouse_ssn_masked ? "Enter last 4 to replace" : "Last 4 (e.g. 6789)"} inputMode="numeric" maxLength={4} />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label className={label}>Referral source</label>
            <input data-testid="cd-referral" className={inputCls} value={form.referral_source} onChange={(e) => set("referral_source", e.target.value)} placeholder="How did they hear about us?" />
          </div>
        </div>

        <div>
          <label className={label}>Notes</label>
          <textarea data-testid="cd-notes" rows={4} className={inputCls} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
        </div>

        <div>
          <label className={label}>Tax situation (drives the estimate)</label>
          <div className="space-y-6">
            {COMPLEXITY_CATEGORIES.map((cat) => {
              const items = SELECTABLE.filter((i) => i.cat === cat.id);
              if (items.length === 0) return null;
              return (
                <div key={cat.id}>
                  <p className="mb-2 text-sm font-medium text-steel-text">{cat.label}</p>
                  <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {items.map((item) => {
                      const on = selected.includes(item.id);
                      const fee = (fees?.items || []).find((f) => f.id === item.id);
                      return (
                        <button
                          key={item.id}
                          type="button"
                          data-testid={`cd-item-${item.id}`}
                          onClick={() => toggleItem(item.id)}
                          className={`flex items-center justify-between gap-3 border px-4 py-2.5 text-left text-sm transition-colors ${
                            on ? "border-steel-brass bg-steel-brass/10 text-steel-text" : "border-steel-border text-steel-secondary hover:border-steel-muted"
                          }`}
                        >
                          <span>{item.label}</span>
                          {fee && (
                            <span className="shrink-0 font-mono text-[10px] text-steel-muted">
                              +${Number(fee.min).toLocaleString()}–${Number(fee.max).toLocaleString()}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div>
          <label className={label}>States filed (outside NH)</label>
          <StatePicker selected={states} onChange={setStates} />
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="sticky top-8 border border-steel-border bg-steel-surface p-7">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-steel-muted">Current estimate</p>
          {estimate ? (
            <p className="neon-text mt-3 font-display text-4xl font-medium text-steel-brass" data-testid="cd-estimate">
              ${estimate.min.toLocaleString()} – ${estimate.max.toLocaleString()}
            </p>
          ) : (
            <p className="mt-3 text-sm text-steel-muted">Loading fees…</p>
          )}
          <p className="mt-3 text-xs leading-relaxed text-steel-muted">
            Updates live as you edit the tax situation. Save first, then email the client if you want them to see it.
          </p>

          <button
            type="button"
            onClick={save}
            disabled={saving}
            data-testid="cd-save"
            className="mt-6 flex w-full items-center justify-center gap-2 border border-steel-brass bg-steel-brass/10 px-6 py-3.5 text-sm font-medium text-steel-text transition-colors hover:bg-steel-brass/20 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            {saving ? "Saving…" : "Save details"}
          </button>
          <button
            type="button"
            onClick={emailEstimate}
            disabled={sending}
            data-testid="cd-send-estimate"
            className="mt-3 flex w-full items-center justify-center gap-2 border border-steel-border px-6 py-3 text-sm text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {sending ? "Sending…" : "Email updated estimate to client"}
          </button>
          <div className="mt-6 border border-steel-border bg-steel-elevated p-5" data-testid="cd-satisfaction">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-steel-muted">Client satisfaction</p>
            <div className="mt-3">
              <SatisfactionMeter readOnly value={client.profile?.satisfaction?.score || 0} />
            </div>
            {client.profile?.satisfaction?.comment && (
              <p className="mt-3 text-xs italic leading-relaxed text-steel-secondary">
                “{client.profile.satisfaction.comment}”
              </p>
            )}
          </div>

          <button
            type="button"
            onClick={resendWelcome}
            disabled={resending}
            data-testid="cd-resend-welcome"
            className="mt-3 flex w-full items-center justify-center gap-2 border border-steel-border px-6 py-3 text-sm text-steel-secondary transition-colors hover:border-steel-brass hover:text-steel-brass disabled:opacity-50"
          >
            {resending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            {resending ? "Resending…" : "Resend welcome email (new temp password)"}
          </button>
          {newTempPassword && (
            <div className="mt-3 border border-steel-brass/40 bg-steel-elevated p-4" data-testid="cd-new-temp-password">
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-muted">New temporary password</p>
              <p className="mt-1 font-mono text-base text-steel-text">{newTempPassword}</p>
              <p className="mt-2 text-xs leading-relaxed text-steel-muted">
                The client's old password no longer works. Keep this only as a backup — they set their own on first
                sign-in.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
